import { useCallback, useEffect, useMemo } from 'react';
import { useLocalStorageState } from '../../../hooks/useLocalStorageState';
import {
  type DashboardSection,
  type SectionId,
  visibleSections,
} from '../sections';

/** localStorage key (versioned, namespaced — see SPEC Q1). */
export const SECTION_FILTERS_STORAGE_KEY = 'tl:dashboard:sections:v1';

export interface UseSectionFiltersResult {
  /** Sections whose permission gate passes for the current user. */
  sections: DashboardSection[];
  /** IDs the user has currently chosen to show, clamped to gated-in sections. */
  visibleSet: Set<SectionId>;
  /** True when the section's gate passes AND it is in the user's visible set. */
  isVisible: (id: SectionId) => boolean;
  /** Toggle a single section in the visible set. */
  toggle: (id: SectionId) => void;
  /** Reset to "everything visible" (gated sections only). */
  showAll: () => void;
  /**
   * The chip row should hide itself when the user only has access to ≤2
   * sections — there's nothing meaningful to filter (prototype behaviour).
   */
  showChipRow: boolean;
}

/**
 * Drives the dashboard section-filter chip row.
 *
 * - Permission gate is the floor: a section the user lacks permission for is
 *   never reachable, no matter what's in localStorage.
 * - The persisted set is the user's *visible* choice. We store the visible
 *   set (not a hidden set) so the default — fresh user, no localStorage —
 *   is "everything I have access to is on".
 * - On every render we clamp the stored set against the live gated-in set,
 *   so a section the user lost permission for cannot be revealed by stale
 *   storage, and a section newly granted appears immediately (no rehydrate
 *   needed).
 */
export function useSectionFilters(
  permissions: string[],
): UseSectionFiltersResult {
  const sections = useMemo(() => visibleSections(permissions), [permissions]);

  const allIds = useMemo(
    () => sections.map((section) => section.id),
    [sections],
  );

  const [storedIds, setStoredIds] = useLocalStorageState<SectionId[]>(
    SECTION_FILTERS_STORAGE_KEY,
    () => allIds,
  );

  // Clamp the stored set: only IDs the user currently has access to.
  const visibleSet = useMemo(() => {
    const allowed = new Set<SectionId>(allIds);
    const filtered = storedIds.filter((id) => allowed.has(id));
    return new Set<SectionId>(filtered);
  }, [storedIds, allIds]);

  // If the clamped set differs from what's persisted (stale entry, lost
  // permission, …), write back the clamped set so the user's next session
  // sees a clean state. Compare by serialised form to avoid an infinite loop
  // when the two sets are equal but stored as a different array reference.
  useEffect(() => {
    const sorted = Array.from(visibleSet).sort();
    const storedSorted = [...storedIds].sort();
    if (
      sorted.length !== storedSorted.length ||
      sorted.some((id, i) => id !== storedSorted[i])
    ) {
      setStoredIds(sorted);
    }
  }, [visibleSet, storedIds, setStoredIds]);

  const toggle = useCallback(
    (id: SectionId) => {
      setStoredIds((prev) => {
        const next = new Set<SectionId>(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return Array.from(next);
      });
    },
    [setStoredIds],
  );

  const showAll = useCallback(() => {
    setStoredIds(allIds);
  }, [setStoredIds, allIds]);

  const isVisible = useCallback(
    (id: SectionId) => visibleSet.has(id),
    [visibleSet],
  );

  return {
    sections,
    visibleSet,
    isVisible,
    toggle,
    showAll,
    showChipRow: sections.length > 2,
  };
}
